/** @format */
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import 'fontsource-roboto';
import { Grid, Box, TextField, Typography } from '@mui/material';
import Sidebar from '../../sidebar/Sidebar';
import clientHelper from '../../../adminHelper/clientList';
import SearchIcon from '@mui/icons-material/Search';
import '../legacy/Client.css';
import SublyApi from '../../../helpers/Api';
import newLogo from '../../../assets/marketing/newLogo.webp';
import Button from '@mui/material/Button';
import { useSelector, useDispatch } from 'react-redux';
import { getAllUsers } from '../../../store/user';
import { startImpersonation } from '../../../store/admin';
import Card from '../../../components/ui/card';
import Input from '../../../components/ui/input';
import Avatar from '../../../components/ui/avatar';
import {
	FiSearch,
	FiPlus,
	FiChevronLeft,
	FiChevronRight,
	FiEye,
} from 'react-icons/fi';
import {
	STATUS_CONFIG,
	STATUS_FILTER_OPTIONS,
} from '../../../constants/userStatus';

// Helper function to get primary headshot for a client
const getPrimaryHeadshot = async (client, token) => {
	try {
		if (!client?.username || !token) return null;

		const response = await SublyApi.getUserImages(
			client.username,
			{ imageType: 'headshot', organized: true },
			token
		);

		const headshotImages = response.images?.headshot || [];
		if (!Array.isArray(headshotImages) || headshotImages.length === 0) {
			return null;
		}

		// Find primary image or use first available
		const primaryImage =
			headshotImages.find((img) => img.isPrimary) || headshotImages[0];
		return primaryImage?.imageUrl || null;
	} catch (error) {
		console.error('Error fetching headshot for', client.username, error);
		return null;
	}
};

// Component for individual client card with image loading
const ClientCard = ({ client, token }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const [headshotUrl, setHeadshotUrl] = useState(null);
	const [imageLoading, setImageLoading] = useState(true);
	const [impersonateError, setImpersonateError] = useState(null);
	const [isImpersonating, setIsImpersonating] = useState(false);

	useEffect(() => {
		const loadHeadshot = async () => {
			setImageLoading(true);
			const primaryHeadshot = await getPrimaryHeadshot(client, token);
			setHeadshotUrl(primaryHeadshot);
			setImageLoading(false);
		};

		loadHeadshot();
	}, [client.username, token]);

	// Get client initials for fallback
	const getClientInitials = () => {
		const firstName = client.firstName || '';
		const lastName = client.lastName || '';

		if (!firstName && !lastName) return 'CL';
		if (!lastName) return firstName.charAt(0).toUpperCase();
		if (!firstName) return lastName.charAt(0).toUpperCase();

		return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
	};

	// Handle impersonation
	const handleImpersonate = async (e) => {
		e.preventDefault(); // Prevent Link navigation
		setImpersonateError(null); // Clear any previous errors
		setIsImpersonating(true);

		try {
			await dispatch(startImpersonation(client.username)).unwrap();
			history.push('/home');
		} catch (error) {
			console.error('Impersonation failed:', error);

			// Extract error message
			const errorMessage = error?.error?.message
				|| error?.message
				|| 'Failed to start impersonation. Please try again.';

			setImpersonateError(errorMessage);

			// Auto-clear error after 5 seconds
			setTimeout(() => setImpersonateError(null), 5000);
		} finally {
			setIsImpersonating(false);
		}
	};

	return (
		<Card key={client.username} className='overflow-hidden rounded-xl'>
			<div className='p-6 flex flex-col items-center text-center'>
				<Avatar className='h-20 w-20 mb-4 ring-2 ring-white shadow-lg border-2 border-gray-200'>
					{!imageLoading && headshotUrl ? (
						<Avatar.Image
							src={headshotUrl}
							alt={`${client.firstName} ${client.lastName}`}
						/>
					) : (
						<Avatar.Fallback className='bg-pink-50 text-primary text-lg font-medium'>
							{getClientInitials()}
						</Avatar.Fallback>
					)}
				</Avatar>

				<h3 className='text-lg font-montserrat-medium text-gray-900'>
					{client.firstName} {client.lastName}
				</h3>

				<p className='text-sm font-montserrat-regular text-gray-500 mt-1 mb-2'>
					{client.email}
				</p>

				{client.profileStatus && (
					<span
						className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold mb-2 ${
							STATUS_CONFIG[client.profileStatus]?.className ||
							STATUS_CONFIG.active.className
						}`}>
						{STATUS_CONFIG[client.profileStatus]?.label || client.profileStatus}
					</span>
				)}

				<div className='mt-auto flex flex-col gap-2 w-full'>
					<Link to={`/admin/clients/client-details/${client.username}`}>
						<Button
							variant='secondary'
							size='sm'
							className='w-full font-montserrat-medium'>
							View Details
						</Button>
					</Link>
					<button
						onClick={handleImpersonate}
						disabled={isImpersonating}
						className={`w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-montserrat-medium transition-colors ${
							isImpersonating
								? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
								: 'text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
						}`}>
						{isImpersonating ? (
							<>
								<svg className='animate-spin mr-2 h-4 w-4' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
									<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
									<path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
								</svg>
								Loading...
							</>
						) : (
							<>
								<FiEye className='mr-2 h-4 w-4' />
								View as User
							</>
						)}
					</button>
					{impersonateError && (
						<div className='w-full px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-600'>
							{impersonateError}
						</div>
					)}
				</div>
			</div>
		</Card>
	);
};

/** component for default screen. It shows sign in buttons when user is signed out.  Shows a welcome message when user is logged in */
const ClientList = () => {
	const dispatch = useDispatch();
	const users = useSelector((st) => st.user.allUsers);
	const token = useSelector((st) => st.admin.token);
	const [clientList, setClientList] = useState(users);
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(12);
	const [statusFilter, setStatusFilter] = useState('active');

	// Fetch users when token or status filter changes
	useEffect(() => {
		if (token) {
			dispatch(getAllUsers({ token, status: statusFilter }));
		}
	}, [token, dispatch, statusFilter]);

	// Update client list when users change
	useEffect(() => {
		setClientList(users);
	}, [users]);

	// Handle search input change
	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
		setCurrentPage(1); // Reset to first page on new search
	};

	// Filter clients based on search term
	const filteredClients = clientList.filter((client) => {
		if (searchTerm === '') {
			return true;
		}

		const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
		const searchLower = searchTerm.toLowerCase();

		return (
			fullName.includes(searchLower) ||
			client.firstName.toLowerCase().includes(searchLower) ||
			client.lastName.toLowerCase().includes(searchLower) ||
			client.email.toLowerCase().includes(searchLower)
		);
	});

	// Pagination logic
	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = filteredClients.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

	// Change page
	const paginate = (pageNumber) => setCurrentPage(pageNumber);

	return (
		<div className='space-y-6'>
			{/* Header with search and actions */}
			<div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
				<div className='relative w-full sm:w-96'>
					<div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
						<FiSearch className='h-5 w-5 text-gray-400' />
					</div>
					<Input
						type='text'
						placeholder='Search clients...'
						className='pl-10 font-montserrat-regular'
						value={searchTerm}
						onChange={handleSearchChange}
					/>
				</div>

				<div className='flex gap-2'>
					<select
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value);
							setCurrentPage(1);
						}}
						className='px-3 py-2 border border-gray-300 rounded-md text-sm font-montserrat-medium bg-white focus:ring-2 focus:ring-gray-400 focus:outline-none'
						aria-label='Filter by status'>
						{STATUS_FILTER_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<Link to='/admin/create-user'>
						<Button
							variant='primary'
							size='sm'
							className='font-montserrat-medium'>
							<FiPlus className='mr-2 h-4 w-4' />
							Add Client
						</Button>
					</Link>
				</div>
			</div>

			{/* Client grid */}
			<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
				{currentItems.length > 0 ? (
					currentItems.map((client) => (
						<ClientCard key={client.username} client={client} token={token} />
					))
				) : (
					<div className='col-span-full flex justify-center items-center p-8'>
						<p className='text-gray-500 font-montserrat-regular'>
							No clients found matching your search.
						</p>
					</div>
				)}
			</div>

			{/* Pagination */}
			{filteredClients.length > itemsPerPage && (
				<div className='flex justify-center mt-6'>
					<nav className='flex items-center'>
						<Button
							variant='secondary'
							size='sm'
							onClick={() => paginate(currentPage - 1)}
							disabled={currentPage === 1}
							className='mr-2 font-montserrat-medium'>
							<FiChevronLeft className='mr-1 h-4 w-4' />
							Previous
						</Button>

						<div className='flex space-x-1'>
							{Array.from({ length: totalPages }, (_, i) => i + 1)
								.filter((num) => {
									// Show first page, last page, current page, and pages around current page
									return (
										num === 1 ||
										num === totalPages ||
										(num >= currentPage - 1 && num <= currentPage + 1)
									);
								})
								.map((number) => {
									// Add ellipsis
									const pages = [];
									if (
										number > 1 &&
										!Array.from({ length: totalPages }, (_, i) => i + 1)
											.filter((num) => {
												return (
													num === 1 ||
													num === totalPages ||
													(num >= currentPage - 1 && num <= currentPage + 1)
												);
											})
											.includes(number - 1)
									) {
										pages.push(
											<span
												key={`ellipsis-${number}`}
												className='px-2 text-gray-500'>
												...
											</span>
										);
									}
									pages.push(
										<Button
											key={number}
											variant={currentPage === number ? 'primary' : 'secondary'}
											size='sm'
											onClick={() => paginate(number)}
											className='mx-1 font-montserrat-medium'>
											{number}
										</Button>
									);
									return pages;
								})}
						</div>

						<Button
							variant='secondary'
							size='sm'
							onClick={() => paginate(currentPage + 1)}
							disabled={currentPage === totalPages}
							className='ml-2 font-montserrat-medium'>
							Next
							<FiChevronRight className='ml-1 h-4 w-4' />
						</Button>
					</nav>
				</div>
			)}
		</div>
	);
};

export default ClientList;
